'use client';

import { useUserStore } from '@unifyapps/carbon/auth/UserStoreProvider';
import { InterfaceAppResourceLoader } from '@unifyapps/carbon/no-code/components/app/InterfaceAppResourceLoader';
import { useRegistryContext } from '@unifyapps/carbon/no-code/context/RegistryProvider';
import type { DeviceDetailsType } from '@unifyapps/carbon/no-code/stores/GlobalStateStore';
import type { ApplicationType, ColorScheme } from '@unifyapps/defs/types/interface';
import type { ReactNode } from 'react';
import { matrixClient, matrixMode } from '../constants';
import { useAppResourceLoading } from '../hooks/useAppResourceLoading';
import { RegistryProviderLoader } from '../RegistryProvider';

function InterfaceAppContainer({
  children,
  interfaceId,
  locale,
  colorScheme,
  interfaceThemeId,
  deviceDetails,
}: {
  children: ReactNode;
  interfaceId: string;
  locale?: string;
  colorScheme?: ColorScheme;
  interfaceThemeId?: string;
  deviceDetails?: DeviceDetailsType;
}) {
  const currentUserDetails = useUserStore().hooks.useCurrentUserDetails();
  const { registry } = useRegistryContext();

  const response = useAppResourceLoading({ interfaceId, locale });

  return (
    <InterfaceAppResourceLoader
      client={matrixClient}
      colorScheme={colorScheme}
      deviceDetails={deviceDetails}
      interfaceThemeId={interfaceThemeId}
      // we want to remount the component when the locale changes so that the translations are updated
      key={currentUserDetails?.user?.locale}
      locale={locale}
      mode={matrixMode}
      registry={registry}
      response={response}
    >
      {children}
    </InterfaceAppResourceLoader>
  );
}

function InterfaceAppContainerWithRegistry(props: {
  children: ReactNode;
  interfaceId: string;
  locale?: string;
  colorScheme?: ColorScheme;
  interfaceThemeId?: string;
  platform: ApplicationType;
  deviceDetails?: DeviceDetailsType;
}) {
  const { children, interfaceId, locale, colorScheme, interfaceThemeId, platform, deviceDetails } =
    props;
  return (
    <RegistryProviderLoader platform={platform}>
      <InterfaceAppContainer
        colorScheme={colorScheme}
        deviceDetails={deviceDetails}
        interfaceId={interfaceId}
        interfaceThemeId={interfaceThemeId}
        locale={locale}
      >
        {children}
      </InterfaceAppContainer>
    </RegistryProviderLoader>
  );
}
export default InterfaceAppContainerWithRegistry;
